import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trophy, Timer, Zap } from "lucide-react";
import AudioToggle from "../components/AudioToggle";
import { getLeaderboard } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";

const DIFFICULTY_LABELS = {
  rookie: "Rookie",
  bandit: "Bandit",
  sheriff: "Sheriff",
};

const BoardRow = ({ rank, username, value, suffix = "", meta, isMe }) => (
  <div
    data-testid={`leaderboard-row-${rank}`}
    className="grid grid-cols-[36px_1fr_auto] items-center gap-3 border-b border-dashed border-[color:var(--dark-brown)]/40 py-3 px-2"
    style={{
      background: isMe ? "rgba(230,8,8,0.08)" : (rank % 2 === 0 ? "rgba(124,63,10,0.05)" : "transparent"),
    }}
  >
    <div
      className="font-anton text-2xl text-center"
      style={{
        color: rank === 1 ? "var(--red)" : "var(--deepest-brown)",
      }}
    >
      {rank}
    </div>
    <div className="min-w-0">
      <div className="font-anton text-lg text-[color:var(--deepest-brown)] truncate">
        {username}
        {isMe && <span className="ml-2 font-elite text-[10px] text-[color:var(--red)] tracking-widest">YOU</span>}
      </div>
      {meta && (
        <div className="font-elite text-[11px] text-[color:var(--dark-brown)] mt-0.5 truncate">{meta}</div>
      )}
    </div>
    <div className="font-anton text-xl text-[color:var(--red)] whitespace-nowrap">
      {value}
      <span className="text-xs ml-1 text-[color:var(--dark-brown)]">{suffix}</span>
    </div>
  </div>
);

const Leaderboard = () => {
  const navigate = useNavigate();
  const { username } = usePlayer();
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getLeaderboard(period)
      .then(setData)
      .catch(() => setData({ fastest: [], most_wins: [] }))
      .finally(() => setLoading(false));
  }, [period]);

  return (
    <div className="min-h-screen bg-yellow-brand">
      <div className="px-5 sm:px-12 py-6 flex items-center justify-between">
        <button
          data-testid="leaderboard-back-button"
          onClick={() => navigate(-1)}
          className="font-elite text-sm flex items-center gap-2 text-[color:var(--deepest-brown)] hover:text-[color:var(--red)]"
        >
          <ArrowLeft size={16} /> back
        </button>
        <AudioToggle ambient />
      </div>

      <div className="text-center max-w-3xl mx-auto px-5 mt-2 mb-6">
        <div className="font-elite text-xs tracking-[0.3em] text-[color:var(--deepest-brown)] flex items-center justify-center gap-2">
          <Trophy size={14} /> — TOP OF THE FRONTIER —
        </div>
        <h2
          className="font-display text-4xl sm:text-6xl mt-2"
          style={{ color: "var(--red)", textShadow: "2px 2px 0 var(--yellow), 4px 4px 0 var(--deepest-brown)" }}
        >
          THE LEADERBOARD
        </h2>
        <p className="font-elite text-sm mt-3 text-[color:var(--deepest-brown)]">
          Fastest fingers in the west. Who's the sauciest slurper on the frontier?
        </p>

        <div className="mt-4 inline-flex border-3 border-[color:var(--deepest-brown)] bg-white" style={{ borderWidth: 3, borderStyle: "solid" }}>
          <button
            data-testid="period-week"
            onClick={() => setPeriod("week")}
            className={`px-4 py-2 font-anton text-sm tracking-wide ${period === "week" ? "bg-[color:var(--red)] text-white" : "text-[color:var(--deepest-brown)]"}`}
          >
            THIS WEEK
          </button>
          <button
            data-testid="period-all"
            onClick={() => setPeriod("all")}
            className={`px-4 py-2 font-anton text-sm tracking-wide ${period === "all" ? "bg-[color:var(--red)] text-white" : "text-[color:var(--deepest-brown)]"}`}
          >
            ALL-TIME
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto px-5 pb-12">
        {/* Fastest reaction */}
        <div className="bg-[color:var(--bone)] border-4 border-[color:var(--deepest-brown)] p-5" style={{ boxShadow: "8px 8px 0 0 rgba(58,29,4,0.45)" }}>
          <div className="flex items-center gap-2 font-anton text-2xl text-[color:var(--deepest-brown)] mb-3">
            <Timer size={22} color="var(--red)" /> FASTEST DRAW
          </div>
          <div className="font-elite text-xs text-[color:var(--dark-brown)] mb-3">
            best single reaction time
          </div>
          {loading ? (
            <div className="font-elite text-sm py-8 text-center">tallying...</div>
          ) : data && data.fastest.length === 0 ? (
            <div className="font-elite text-sm py-8 text-center text-[color:var(--dark-brown)]" data-testid="fastest-empty">
              No slurps recorded this {period === "week" ? "week" : "frontier"}. Be the first.
            </div>
          ) : (
            <div data-testid="fastest-list">
              {data.fastest.map((row, i) => (
                <BoardRow
                  key={`f-${row.username}`}
                  rank={i + 1}
                  username={row.username}
                  value={row.best}
                  suffix="ms"
                  meta={`vs ${DIFFICULTY_LABELS[row.difficulty] || row.difficulty || "—"}`}
                  isMe={row.username === username}
                />
              ))}
            </div>
          )}
        </div>

        {/* Most wins */}
        <div className="bg-[color:var(--bone)] border-4 border-[color:var(--deepest-brown)] p-5" style={{ boxShadow: "8px 8px 0 0 rgba(58,29,4,0.45)" }}>
          <div className="flex items-center gap-2 font-anton text-2xl text-[color:var(--deepest-brown)] mb-3">
            <Zap size={22} color="var(--red)" /> MOST WINS
          </div>
          <div className="font-elite text-xs text-[color:var(--dark-brown)] mb-3">
            duels won {period === "week" ? "this week" : "all-time"}
          </div>
          {loading ? (
            <div className="font-elite text-sm py-8 text-center">tallying...</div>
          ) : data && data.most_wins.length === 0 ? (
            <div className="font-elite text-sm py-8 text-center text-[color:var(--dark-brown)]" data-testid="wins-empty">
              No wins recorded yet. The frontier awaits.
            </div>
          ) : (
            <div data-testid="wins-list">
              {data.most_wins.map((row, i) => (
                <BoardRow
                  key={`w-${row.username}`}
                  rank={i + 1}
                  username={row.username}
                  value={row.wins}
                  suffix={row.wins === 1 ? "win" : "wins"}
                  meta={`${row.matches} duel${row.matches === 1 ? "" : "s"} fought`}
                  isMe={row.username === username}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
